import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import { _getAppliedShifts } from "@/services/clientcandidates";
import {
  _fetchApplicantLists,
  _fetchContractShifts,
  _fetchTerminatedShifts,
  _fetchContractDetail,
} from "@/services/clientcontractdetail";
import { handleAPICatch } from "@/utilities/utils";

import {
  formatContractApplicantLists,
  formatContractShifts,
  formatContractTerminatedShifts,
} from "../utilities/dataFormatter";
let defaultPagination = {
  currentPage: 1,
  totalPage: 1,
  isLastPage: false,
};
const initialState = {
  isLoading: true,
  isApplicationLoading: true,
  isShiftLoading: true,
  isTerminatedLoading: true,
  contractId: null,
  contractDetails: {},
  activeContract: "",
  runningLateContract: {},
  isContractInfoLoading: false,
  // Pagination
  shiftsPagination: defaultPagination,
  applicantsPagination: defaultPagination,
  terminatedShiftsPagination: defaultPagination,

  // Listings
  contractShiftsLists: [],
  contractTerminatedShiftsLists: [],
  contractApplicantsLists: [],

  // Filters
  filters: {
    search: "",
    shiftDates: [],
    terminatesShiftDates: [],
  },
  shiftListSorting: {
    sortByColumn: "",
    sortByOrder: "",
  },

  terminatedListSorting: {
    sortByColumn: "",
    sortByOrder: "",
  },

  applicantListSorting: {
    sortByColumn: "",
    sortByOrder: "",
  },

  // Selection
  selectedShifts: [],
  selectedTerminatedShifts: [],
  selectedApplication: [],
  toggleSelected: false,

  // Total Count
  totalApplicants: 0,
  totalShifts: 0,
  totalTerminatedShifts: 0,

  // Select All
  isApplicantsSelectedAll: false,
  isShiftsSelectedAll: false,
  isTerminatedShiftsSelectedAll: false,
};

export const fetchContractDetailsData = createAsyncThunk(
  "clientContractDetail/fetchContractDetailsData",
  async (data, { dispatch }) => {
    dispatch(setContractInfoLoading(true));
    const { contract_id } = data;
    const response = _fetchContractDetail({ contract_id });
    response
      .then((res) => {
        const {
          data: { contract_details, running_late },
        } = res;
        dispatch(setContractId(contract_id));
        dispatch(updateContractDetails(contract_details));
        dispatch(updateRunningLateCandidateContract(running_late));
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        dispatch(setContractInfoLoading(false));
      });
  },
);

export const fetchTerminatedShifts = createAsyncThunk(
  "clientContractDetail/fetchTerminatedShifts",
  async (data, thunkAPI) => {
    let paginationData =
      thunkAPI.getState().clientContractDetail.terminatedShiftsPagination;
    let contractId = data.contract_id
      ? data.contract_id
      : thunkAPI.getState().clientContractDetail.contractId;
    let filters = thunkAPI.getState().clientContractDetail.filters;
    let sortingData =
      thunkAPI.getState().clientContractDetail.terminatedListSorting;
    if (paginationData.isLastPage) {
      return thunkAPI.dispatch(setTerminatedLoading(false));
    }
    const _data = {
      contract_id: contractId,
      currentPage: paginationData.currentPage,
      dates: filters.terminatesShiftDates.join(","),
      ...sortingData,
    };
    const response = _fetchTerminatedShifts(_data);
    response
      .then((res) => {
        const {
          data: { contract_terminated_shifts = [], meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        const data = formatContractTerminatedShifts(contract_terminated_shifts);
        thunkAPI.dispatch(
          updateTerminatedShiftsPagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList =
          currentPage === 1
            ? []
            : _.uniq(
                thunkAPI.getState().clientContractDetail
                  .contractTerminatedShiftsLists,
              );
        let firstshift_id = data.length > 0 ? data[0].shift_id : null;
        thunkAPI.dispatch(updateTotalTerminatedShifts(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.shift_id === firstshift_id) !== undefined
        ) {
          //
        } else {
          thunkAPI.dispatch(updateTerminatedshifts([...prevList, ...data]));
        }
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          thunkAPI.dispatch(setTerminatedLoading(false));
        }, 20);
        // thunkAPI.dispatch(updateLoadingCount(curLoadingCount - 1));
      });
  },
);

export const fetchContractShifts = createAsyncThunk(
  "clientContractDetail/fetchContractShifts",
  async (data, thunkAPI) => {
    let paginationData =
      thunkAPI.getState().clientContractDetail.shiftsPagination;
    let contractId = data.contract_id
      ? data.contract_id
      : thunkAPI.getState().clientContractDetail.contractId;
    let filters = thunkAPI.getState().clientContractDetail.filters;
    let sortingData = thunkAPI.getState().clientContractDetail.shiftListSorting;
    // let curLoadingCount = thunkAPI.getState().clientContractDetail.loadingCount;
    if (paginationData.isLastPage) {
      return thunkAPI.dispatch(setShiftLoading(false));
    }
    // thunkAPI.dispatch(updateLoadingCount(curLoadingCount + 1));
    const _data = {
      contract_id: contractId,
      currentPage: paginationData.currentPage,
      dates: filters.shiftDates.join(","),
      ...sortingData,
    };
    const response = _fetchContractShifts(_data);
    response
      .then((res) => {
        const {
          data: { contract_shifts = [], meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        const data = formatContractShifts(contract_shifts);
        thunkAPI.dispatch(
          updateShiftsPagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList =
          currentPage === 1
            ? []
            : _.uniq(
                thunkAPI.getState().clientContractDetail.contractShiftsLists,
              );
        let firstshift_id = data.length > 0 ? data[0].shift_id : null;
        thunkAPI.dispatch(updateTotalShifts(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.shift_id === firstshift_id) !== undefined
        ) {
          //
        } else {
          thunkAPI.dispatch(updateContractShifts([...prevList, ...data]));
        }
        return res;
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          thunkAPI.dispatch(setShiftLoading(false));
        }, 20);
        // thunkAPI.dispatch(updateLoadingCount(curLoadingCount - 1));
      });
  },
);
export const fetchApplicantLists = createAsyncThunk(
  "clientContractDetail/fetchApplicantLists",
  async (data, { dispatch, getState }) => {
    let clientContractDetail = getState().clientContractDetail;
    let paginationData = clientContractDetail.applicantsPagination;
    let contractId = data.contract_id
      ? data.contract_id
      : clientContractDetail.contractId;
    let contractStatus = clientContractDetail.contractDetails.contract_status;
    let filters = clientContractDetail.filters;
    let sortingData = clientContractDetail.applicantListSorting;

    if (paginationData.isLastPage) {
      return dispatch(setApplicationLoading(false));
    }

    const _data = {
      contract_id: contractId,
      contract_status: data.contract_status || contractStatus,
      currentPage: paginationData.currentPage,
      search: filters.search,
      ...sortingData,
    };
    const response = _fetchApplicantLists(_data);
    response
      .then((res) => {
        const {
          data: { contract_applicant_list = [], meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        const data = formatContractApplicantLists(contract_applicant_list);
        dispatch(
          updateApplicantsPagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );

        let prevList =
          currentPage === 1
            ? []
            : _.uniq(getState().clientContractDetail.contractApplicantsLists);
        let firstapplicant_id = data.length > 0 ? data[0].applicant_id : null;
        dispatch(updateTotalApplicants(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find((item) => item.applicant_id === firstapplicant_id) !==
            undefined
        ) {
          //
        } else {
          dispatch(updateContractApplicants([...prevList, ...data]));
        }
        return res;
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setApplicationLoading(false));
        }, 20);
        // dispatch(setLoading(false));
        // dispatch(updateLoadingCount(curLoadingCount - 1));
      });
  },
);
//
export const fetchSingleContractShift = createAsyncThunk(
  "clientContractDetail/fetchSingleContractShift",
  async (data, thunkAPI) => {
    const { candidate_id } = data;
    const response = _getAppliedShifts(data);
    response
      .then((res) => {
        const {
          data: { data },
        } = res;
        thunkAPI.dispatch(
          updateSingleContractShift({
            candidate_id: candidate_id,
            shifts: data,
          }),
        );
        // thunkAPI.dispatch(setAppliedCandidatesLoading(false));
        // return res;
      })
      .catch((error) => {
        // handleAPICatch(error);
        return thunkAPI.rejectWithValue(error);
      });
  },
);

const clientContractDetailSlice = createSlice({
  name: "clientContractDetail",
  initialState,
  reducers: {
    /**
     * Set the contract ID in the state.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object containing the payload
     * @return {type} undefined
     */
    setContractId: (state, action) => {
      state.contractId = action.payload;
    },
    /**
     * Update contract details.
     *
     * @param {type} state - description of state parameter
     * @param {type} action - description of action parameter
     * @return {type} description of return value
     */
    updateContractDetails: (state, action) => {
      state.contractDetails = action.payload;
    },
    updateRunningLateCandidateContract: (state, action) => {
      state.runningLateContract = action.payload;
    },
    updateContractShifts: (state, action) => {
      state.contractShiftsLists = action.payload;
    },
    /**
     * Update the contract applicants in the state.
     *
     * @param {type} state - The current state
     * @param {type} action - The action containing the payload
     * @return {type} undefined
     */
    updateContractApplicants: (state, action) => {
      state.contractApplicantsLists = action.payload;
    },
    /**
     * Update the active contract in the state.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object
     * @return {type} undefined
     */
    updateActiveContract: (state, action) => {
      state.activeContract = action.payload;
    },
    /**
     * Sets the loading state based on the provided action payload.
     *
     * @param {type} state - the current state object
     * @param {type} action - the action object containing payload
     * @return {type} undefined
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setApplicationLoading: (state, action) => {
      state.isApplicationLoading = action.payload;
    },

    setShiftLoading: (state, action) => {
      state.isShiftLoading = action.payload;
    },

    setTerminatedLoading: (state, action) => {
      state.isTerminatedLoading = action.payload;
    },

    setContractInfoLoading: (state, action) => {
      state.isContractInfoLoading = action.payload;
    },
    /**
     * Updates the shift for a single contract based on the provided candidate ID and shifts.
     *
     * @param {object} state - The current state of the application
     * @param {object} action - The action object containing payload with candidate ID and shifts
     */
    updateSingleContractShift: (state, action) => {
      const { candidate_id, shifts } = action.payload;
      let applicantsClone = [...state.contractApplicantsLists];
      state.contractApplicantsLists = applicantsClone.map((contract) => {
        if (contract.candidate_id == candidate_id) {
          contract.shifts = shifts;
        }
        return contract;
      });
    },
    /**
     * Manage the expansion of a row in the state.
     *
     * @param {object} state - the current state
     * @param {object} action - the action to be performed
     * @return {void}
     */
    manageExpandedRow: (state, action) => {
      const { candidate_id, isExpanded } = action.payload;
      let applicantsClone = [...state.contractApplicantsLists];
      state.contractApplicantsLists = applicantsClone.map((contract) => {
        if (contract.candidate_id == candidate_id) {
          contract.isExpanded = isExpanded;
        }
        return contract;
      });
    },
    /**
     * Update the filters in the state with the provided payload.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object containing the payload
     * @return {void}
     */
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    /**
     * Updates the pagination of shifts in the state with the given action payload.
     *
     * @param {Object} state - the current state object
     * @param {Object} action - the action object containing the payload
     */
    updateShiftsPagination: (state, action) => {
      state.shiftsPagination = {
        ...state.shiftsPagination,
        ...action.payload,
      };
    },
    /**
     * Updates the pagination for the list of applicants in the state.
     *
     * @param {object} state - the current state of the application
     * @param {object} action - the action containing the payload to update the pagination
     * @return {void}
     */
    updateApplicantsPagination: (state, action) => {
      state.applicantsPagination = {
        ...state.applicantsPagination,
        ...action.payload,
      };
    },
    updateTerminatedShiftsPagination: (state, action) => {
      state.terminatedShiftsPagination = {
        ...state.terminatedShiftsPagination,
        ...action.payload,
      };
    },
    updateShiftListSorting: (state, action) => {
      state.shiftListSorting = action.payload;
    },
    updateTerminatedListSorting: (state, action) => {
      state.terminatedListSorting = action.payload;
    },
    //
    updateApplicantListSorting: (state, action) => {
      state.applicantListSorting = action.payload;
    },

    updateTerminatedshifts: (state, action) => {
      state.contractTerminatedShiftsLists = action.payload;
    },
    updateSelectedApplication: (state, action) => {
      state.selectedApplication = action.payload;
    },
    updateSelectedShifts: (state, action) => {
      state.selectedShifts = action.payload;
    },
    updateSelectedTerminatedShifts: (state, action) => {
      state.selectedTerminatedShifts = action.payload;
    },
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    updateLoadingCount: (state, action) => {
      // state.loadingCount = action.payload;
    },
    resetContractDetail: (state) => {
      state.contractId = null;
      state.applicantsPagination = initialState.applicantsPagination;
      state.contractApplicantsLists = [];
      state.contractDetails = {};
      state.shiftsPagination = initialState.shiftsPagination;
      state.contractShiftsLists = [];
      state.isLoading = true;
      ((state.isApplicationLoading = true),
        (state.isShiftLoading = true),
        (state.isTerminatedLoading = true),
        (state.terminatedShiftsPagination =
          initialState.terminatedShiftsPagination));
      state.contractTerminatedShiftsLists = [];
    },
    updateContractStatus: (state, action) => {
      state.contractDetails.contract_status = action.payload;
    },
    updateTotalApplicants: (state, action) => {
      state.totalApplicants = action.payload;
    },
    updateTotalShifts: (state, action) => {
      state.totalShifts = action.payload;
    },
    updateTotalTerminatedShifts: (state, action) => {
      state.totalTerminatedShifts = action.payload;
    },
    toggleApplicantsSelectAll: (state, action) => {
      state.isApplicantsSelectedAll = action.payload;
    },
    toggleShiftsSelectAll: (state, action) => {
      state.isShiftsSelectedAll = action.payload;
    },
    toggleTerminatedShiftsSelectAll: (state, action) => {
      state.isTerminatedShiftsSelectedAll = action.payload;
    },
    deSelectAllListings: (state) => {
      state.isApplicantsSelectedAll = false;
      state.isShiftsSelectedAll = false;
      state.isTerminatedShiftsSelectedAll = false;
      state.selectedApplication = [];
      state.selectedShifts = [];
      state.selectedTerminatedShifts = [];
      state.toggleSelected = !state.toggleSelected;
    },
  },
});

export const {
  setContractId,
  updateActiveContract,
  setLoading,
  setApplicationLoading,
  setShiftLoading,
  setTerminatedLoading,
  setContractInfoLoading,
  updateContractDetails,
  updateSingleContractShift,
  manageExpandedRow,
  updateLoadingCount,
  updateToggleSelected,
  resetContractDetail,
  updateContractStatus,

  // Filters
  updateFilters,

  //sorting
  updateShiftListSorting,
  updateTerminatedListSorting,
  updateApplicantListSorting,
  updateRunningLateCandidateContract,

  // Listing
  updateContractShifts,
  updateContractApplicants,
  updateTerminatedshifts,

  // Pagination
  updateShiftsPagination,
  updateApplicantsPagination,
  updateTerminatedShiftsPagination,

  // Selection
  updateSelectedApplication,
  updateSelectedShifts,
  updateSelectedTerminatedShifts,
  deSelectAllListings,

  // Total Count
  updateTotalApplicants,
  updateTotalShifts,
  updateTotalTerminatedShifts,

  // Select All
  toggleApplicantsSelectAll,
  toggleShiftsSelectAll,
  toggleTerminatedShiftsSelectAll,
} = clientContractDetailSlice.actions;

export default clientContractDetailSlice.reducer;
